import { yupResolver } from "@hookform/resolvers/yup";
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, Slide, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { connect, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";

import { emailRegex, letterRegex, validSMS } from "../../../config/regex";
import { MEDIA_SIZE, MESSAGE_TYPE } from "../../../constants";
import { useUrlParams } from "../../../hooks";
import { MessageService } from "../../../services";
import { setSimpleValue } from "../../../store/actions";
import { currentPropertySelector } from "../../../store/selectors";
import { getMediaLink } from "../../../utils/mediaLink";
import AddMessageFormV2 from "../Forms/AddMessageFormV2";

const useStyles = makeStyles((theme) => ({
  modalDialog: {
    "& .MuiDialogContent-root": {
      paddingTop: "16px",
    },
    "& > div > .MuiPaper-root": {
      backgroundColor: "rgba(70,70,70) !important",
      padding: "16px",
      borderRadius: "20px",
      gap: "16px",
    },
    "& .MuiDialogActions-root": {
      margin: "0 auto",
      padding: "16px",
      justifyContent: "center",
      width: "100%",
      "@media (max-width:576px)": {
        bottom: 0,
        position: "absolute",
      },
      backgroundColor: "#000",
    },
  },
  modalTitle: {
    color: "#fff",
    fontSize: "24px",
    fontWeight: "700",
    backgroundColor: "#000",
  },
  modalContent: {
    position: "relative",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000",
  },
  avatarContent: {
    textAlign: "center",
    verticalAlign: "middle",
    height: "100%",
    marginRight: "16px",
  },
  avatar: {
    width: 160,
    height: 160,
    margin: "20px 100px",
  },
  formContent: {
    margin: "auto",
    height: "100%",
    width: "300px",
    backgroundColor: "#000",
    alignItems: "center",
    "@media (max-width:576px)": {
      maxWidth: "350px",
      width: "100%",
    },
  },
  button: {
    bottom: 0,
    right: 0,
    padding: "8px 12px",
    backgroundColor: theme.palette.semanticSurface.dark,
    color: "#A2A2A2",
    textAlign: "center",
    width: "100%",
    fontWeight: "bold",
    fontSize: "16px",
    borderRadius: 32,
    transition: "background-color 0.3s ease",
    "@media (max-width:576px)": {
      width: "100%",
    },
    "&:hover": {
      backgroundColor: theme.palette.semanticNeutral.main,
    },
    "&.submitButton": {
      backgroundColor: "#F50561",
      "&:hover": {
        backgroundColor: theme.palette.semanticPrimary.main,
      },
    },
  },
  disabledButton: {
    color: "rgba(255, 255, 255, .5) !important",
    backgroundColor: `${theme.secondaryColor} !important`,
  },
  avatarInfo: {
    color: "#fff",
    marginBottom: "8px",
  },
  messageContent: {
    width: "676px",
    height: "336px",
    display: "flex-column",
    justifyContent: "center",
    alignContent: "center",
  },
  toast: {
    marginBottom: "0 !important",
    marginTop: "1em !important",
    backgroundRepeat: "no-repeat !important",
    backgroundPosition: "1rem !important",
    backgroundSize: "2rem !important",
  },
  toastBody: {
    textAlign: "left",
  },
  toastProgress: {
    background: "linear-gradient(to right, #97023b, #fff) !important",
  },
}));

const phoneDefaultValue = {
  code: "+1",
  iso: "US",
  number: "",
};

const determineMessageType = ({ email, phone }) => {
  if (email && phone.number) {
    return MESSAGE_TYPE.BOTH;
  } else if (email) {
    return MESSAGE_TYPE.EMAIL;
  } else if (phone?.number) {
    return MESSAGE_TYPE.TEXT;
  }
};
const formatPhoneNumber = (number) => {
  if (!number) return "";

  const areaCode = number.slice(0, 3);
  const firstPart = number.slice(3, 6);
  const secondPart = number.slice(6, 10);

  return `(${areaCode}) ${firstPart}-${secondPart}`;
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const contactDialogFormSchema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .matches(letterRegex, "Name cannot contain numbers or special characters."),
  phone: yup.object().shape({
    code: yup.string().required("Required"),
    iso: yup.string().required("Required"),
    number: yup
      .string()
      .ensure()
      .when(["code", "iso"], {
        is: (code, iso) => code && iso,
        then: (schema) => {
          return schema.test("is-valid-phone-number", "Please provide a valid phone number", function (value) {
            return yup.string().phone(this.parent.iso, true).isValidSync(value);
          });
        },
      })
      .test("required", "Either phone or email is required", (value, values) => {
        const { email } = values.from[1].value;
        return !!(value || email);
      }),
  }),
  email: yup
    .string()
    .test("required", "Either phone or email is required", (value, values) => {
      const { phone } = values.parent;

      return !!(value || phone.number);
    })
    .test("invalid", "Invalid Email", async (value) => {
      if (value === "") return true;
      return emailRegex.test(value);
    }),
  message: yup.string().matches(validSMS, "Message contains invalid characters.").required("Message is required."),
});

function ContactDialogV2(props) {
  const { shareKey, isDialogOpen, setSimpleValue, createdBy = {} } = props;

  const { propertyId, experienceId } = useUrlParams();
  const { property } = useSelector(currentPropertySelector);
  const classes = useStyles({
    ...props,
  });

  const photo = getMediaLink({ size: MEDIA_SIZE.tiny, link: createdBy?.photoUrl });
  const [createdByPhone = {}] = createdBy?.phones || [];
  const headerRef = useRef();
  const centerRef = useRef();
  const [messageSent, setMessageSent] = useState(false);

  const { handleSubmit, getValues, setValue, control, reset, trigger } = useForm({
    mode: "all",
    resolver: yupResolver(contactDialogFormSchema),
    defaultValues: {
      name: "",
      phone: phoneDefaultValue,
      email: "",
      message: "",
    },
  });

  const values = getValues();

  useEffect(() => {
    trigger().then();
  }, [values.email, values.phone]);

  const onCloseDialog = () => {
    reset();
    setSimpleValue(shareKey, undefined);
    setMessageSent(false);
  };

  const onSubmit = async (values) => {
    const type = determineMessageType(values);
    const { name: senderName, message: senderText, email: senderEmail, phone: senderPhone } = values;

    try {
      const data = {
        type,
        senderName,
        senderText,
        ...(senderEmail && { senderEmail }),
        ...(senderPhone && { senderPhone: senderPhone.number }),
        shareUrl: window.location.href,
      };
      const { err } = await MessageService.addMessage(propertyId, experienceId, data);
      setMessageSent(true);

      if (!err) {
        toast.success(`Sent ${type} to contact`);
      }
    } catch {
      toast.error("Error sending to contact", {
        containerId: "contactDialog",
      });
    }
  };

  const validate = async () => {
    try {
      await trigger();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Dialog
      className={classes.modalDialog}
      open={isDialogOpen}
      onClose={onCloseDialog}
      aria-labelledby="form-dialog-title"
      TransitionComponent={Transition}
      maxWidth="md"
      data-testid="form-dialog-title"
    >
      <DialogTitle id="dialog-title" className={classes.modalTitle} ref={headerRef}>
        Contact
      </DialogTitle>
      {!messageSent ? (
        <DialogContent className={classes.modalContent}>
          <div className={classes.avatarContent}>
            <Avatar src={photo} className={classes.avatar} data-testid="form-dialog-avatar" />
            <Typography className={classes.avatarInfo} variant="h6" sx={{ fontWeight: "bold" }}>
              {createdBy?.name}
            </Typography>
            <Typography className={classes.avatarInfo} variant="body1">
              {formatPhoneNumber(createdByPhone?.number) || ""}
            </Typography>
            <Typography className={classes.avatarInfo} variant="body1" gutterBottom>
              {createdBy?.email}
            </Typography>
          </div>
          <div className={classes.formContent} ref={centerRef}>
            <AddMessageFormV2 control={control} getValues={getValues} setValue={setValue} validate={validate} />
          </div>
        </DialogContent>
      ) : (
        <DialogContent className={classes.modalContent}>
          <div className={classes.messageContent} data-testid="success-message">
            <Typography className={classes.avatarInfo} variant="h6" align="center">
              Your message was sent!
            </Typography>
            <Typography className={classes.avatarInfo} variant="h6" align="center">
              Thank you for your interest in
            </Typography>
            <Typography
              className={classes.avatarInfo}
              variant="h6"
              align="center"
              sx={{ fontSize: "24px", color: "#F50561 !important" }}
            >
              {property?.name}
            </Typography>
            <Typography className={classes.avatarInfo} variant="h6" align="center">
              We will be in touch soon.
            </Typography>
          </div>
        </DialogContent>
      )}

      {!messageSent ? (
        <DialogActions className={classes.btnWrapper}>
          <Button className={classes.button} onClick={onCloseDialog}>
            Cancel
          </Button>
          <Button
            className={`${classes.button} submitButton`}
            sx={{ color: "#fff !important" }}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </DialogActions>
      ) : (
        <DialogActions className={classes.btnWrapper}>
          <Button className={classes.button} onClick={onCloseDialog}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function mapStateToProps(state, ownProps) {
  const { simpleValues } = state;
  const { documentName } = ownProps;

  const shareKey = `contact_${documentName}`;
  const isDialogOpen = !!simpleValues?.[shareKey];

  return {
    isDialogOpen,
    shareKey,
  };
}

export default connect(mapStateToProps, { setSimpleValue })(ContactDialogV2);
